import * as React from 'react';
import './styles.css';
import { SalesStore } from '../../stores/sales-store';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { inject, observer } from 'mobx-react';
import { dateFormatted, toDate } from '../../datetime';
import { globalStyles } from 'src/theme';
import { OrderEventType, OrderStatus } from 'src/models/order';
import {
  IConsignmentNotesPayload,
  IGrnRaisedEventPayload,
  IPaymentPayload,
  IAllocationPayload,
  IPurchaseOrderFailurePayload,
  IPurchaseOrderSuccessPayload,
  ISentToDispatchPayload,
  IShareQuoteEventPayload,
  IUpdatePurchaseOrderPayloadPayload as IUpdatePurchaseOrderPayload,
  IUpdatePurchaseOrderPayloadPayload,
  IWorkflowPayload,
  IManualCompletionPayload,
} from 'src/services/sales-api';
import MuiPickersUtilsProvider from 'material-ui-pickers/MuiPickersUtilsProvider';
import { InlineDatePicker } from 'material-ui-pickers/DatePicker';
import LuxonUtils from '@date-io/luxon';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { DateTime } from 'luxon';
import { UiStore } from 'src/stores/ui-store';
import AuditEventLog, { IAuditEvent } from './AuditEventLog';

interface IOrderAuditInfoProps extends WithStyles<typeof styles> {
  salesStore?: SalesStore;
  uiStore?: UiStore;
}

interface IInvoiceProcessingAuditPayload {
  phase: string;
  outcome: string;
  initiatingRepCode?: string;
  externalOrderNumber?: string;
  operation?: string;
  errorSummary?: string;
}

@inject('salesStore', 'uiStore')
@observer
export class OrderAuditInfo extends React.Component<IOrderAuditInfoProps> {
  constructor(props: IOrderAuditInfoProps) {
    super(props);
    this.handleExipryDateChanged = this.handleExipryDateChanged.bind(this);
  }

  public render() {
    const { salesStore } = this.props;
    return (
      <Grid container className="section audit-info">
        <Grid item xs={1} />
        <Grid item xs={10}>
          <h2>Audit Information</h2>
          <TextField
            className={this.props.classes.textField}
            InputProps={{ disableUnderline: true }}
            label="Owner"
            type="text"
            value={salesStore!.order.owner}
            disabled
            fullWidth
          />
          {salesStore!.order.status === OrderStatus.Quote && (
            <MuiPickersUtilsProvider utils={LuxonUtils}>
              <InlineDatePicker
                className={this.props.classes.inlineDateField}
                onlyCalendar
                label="Quote Expiry"
                value={salesStore!.order.quoteExpiryDate}
                onChange={this.handleExipryDateChanged}
                labelFunc={(date: DateTime) => dateFormatted(date)}
                InputProps={{ disableUnderline: true }}
                leftArrowIcon={<KeyboardArrowLeft />}
                rightArrowIcon={<KeyboardArrowRight />}
                minDate={DateTime.local().minus({ days: 1 })}
                disabled={this.props.uiStore!.orderUiState.formReadOnly}
              />
            </MuiPickersUtilsProvider>
          )}
          <AuditEventLog
            events={salesStore!.order.eventsOrderedByDate.map((event) => ({
              action: OrderEventType[event.eventType],
              date: event.date.toISO(),
              repCode: event.repCode,
              payload: event.payload as unknown as IAuditEvent['payload'],
            }))}
            describeEvent={describeOrderEvent}
          />
        </Grid>
      </Grid>
    );
  }

  private handleExipryDateChanged(date: DateTime) {
    this.props.salesStore!.order.update('quoteExpiryDate', date);
  }
}

export const describeOrderEvent = (auditEvent: IAuditEvent): string => {
  const event = {
    eventType: OrderEventType[
      auditEvent.action as keyof typeof OrderEventType
    ] as OrderEventType,
    repCode: auditEvent.repCode,
    payload: auditEvent.payload as unknown,
  };
  switch (event.eventType) {
      case OrderEventType.Created:
        return `Created by ${event.repCode}`;
      // case OrderEventType.Modified:
      //     return `Modified by ${event.repCode}`;
      case OrderEventType.SharedQuote: {
        const payload = event.payload as IShareQuoteEventPayload;
        return `${event.repCode} shared quote with ${payload.name} - ${payload.email}`;
      }
      case OrderEventType.PurchaseOrderRaised:
        return `PO raised by ${event.repCode}`;
      case OrderEventType.CustomPricingApproved:
        return `Custom pricing approved by ${event.repCode} ${
          event.payload ? (event.payload as IWorkflowPayload).reason : ''
        }`;
      case OrderEventType.CustomPricingRejected:
        return `Custom pricing rejected by ${event.repCode} ${
          event.payload ? (event.payload as IWorkflowPayload).reason : ''
        }`;
      case OrderEventType.OrderDeletionApproved:
        return `Order deletion approved by ${event.repCode} ${
          event.payload ? (event.payload as IWorkflowPayload).reason : ''
        }`;
      case OrderEventType.OrderDeletionRejected:
        return `Order deletion rejected by ${event.repCode} ${
          event.payload ? (event.payload as IWorkflowPayload).reason : ''
        }`;
      case OrderEventType.PurchaseOrderSuccess: {
        const success = event.payload as IPurchaseOrderSuccessPayload;
        return `Purchase Order ${success.purchaseOrderNumber} created for ${success.supplierName}`;
      }
      case OrderEventType.PurchaseOrderFailure: {
        const failure = event.payload as IPurchaseOrderFailurePayload;
        return `Purchase Order Failed for ${failure.supplierName}`;
      }
      case OrderEventType.PurchaseOrderingComplete:
        return `Purchase Ordering complete`;
      case OrderEventType.OrderSplit:
        return `Order split by ${event.repCode}`;
      case OrderEventType.ConsignmentNoteSent: {
        const consignment = event.payload as IConsignmentNotesPayload;
        return `Consignment note(s) emailed by ${event.repCode}. Delivery date: ${
          consignment.dateTba ? 'TBA' : dateFormatted(toDate(consignment.deliveryDate || ''))
        }`;
      }
      case OrderEventType.InDisputeCustomerApproved:
        return `Credit limit approved by ${event.repCode} ${
          event.payload ? (event.payload as IWorkflowPayload).reason : ''
        }`;
      case OrderEventType.SentToDispatch: {
        const dispatch = event.payload as ISentToDispatchPayload;
        return `Sent JobId: ${dispatch.jobId} to dispatch by ${event.repCode}`;
      }
      case OrderEventType.InDisputeCustomerRejected:
        return `Credit limit rejected by ${event.repCode} ${
          event.payload ? (event.payload as IWorkflowPayload).reason : ''
        }`;
      case OrderEventType.InDisputeCustomerAutoApproved:
        return `Credit limit approved automatically`;
      case OrderEventType.InvoiceSent:
        return `Invoice sent to Unleashed`;
      case OrderEventType.InvoiceFailure:
        return `Invoice failure in Unleashed`;
      case OrderEventType.InvoiceProcessing:
        return describeInvoiceProcessingEvent(event.payload as IInvoiceProcessingAuditPayload);
      case OrderEventType.RefundApproved:
        return `Refund approved by ${event.repCode} ${event.payload ? (event.payload as IWorkflowPayload).reason : ''}`;
      case OrderEventType.RefundRejected:
        return `Refund rejected by ${event.repCode} ${event.payload ? (event.payload as IWorkflowPayload).reason : ''}`;
      case OrderEventType.PurchaseOrderEmailed: {
        const p = event.payload as IPurchaseOrderSuccessPayload;
        return `Purchase Order ${p.purchaseOrderNumber} emailed to ${p.supplierName}`;
      }
      case OrderEventType.PaymentSent: {
        const paymentPayload = event.payload as IPaymentPayload;
        if (paymentPayload) {
          return `Payment Id ${paymentPayload!.Id} sent for amount $${parseFloat(paymentPayload!.Amount).toFixed(2)}`;
        }
        return 'Payment Sent';
      }
      case OrderEventType.PaymentComplete: {
        const paymentCompletePayload = event.payload as IPaymentPayload;
        if (paymentCompletePayload) {
          return `Payment Id ${paymentCompletePayload!.Id} complete for amount $${parseFloat(
            paymentCompletePayload!.Amount,
          ).toFixed(2)}`;
        }
        return 'Payment Complete';
      }
      case OrderEventType.PaymentError: {
        const paymentErrorPayload = event.payload as IPaymentPayload;
        if (paymentErrorPayload) {
          return `Payment Id ${paymentErrorPayload!.Id} errored for amount $${parseFloat(
            paymentErrorPayload!.Amount,
          ).toFixed(2)}`;
        }
        return 'Payment Errored';
      }

      case OrderEventType.AllocationSent: {
        const payload = event.payload as IAllocationPayload;

        if (payload) {
          return `Allocation sent for invoice auditNo: ${payload!.invoiceAuditNo}
            and receipt auditNo: ${payload!.receiptAuditNo}`;
        }

        return 'Allocation Sent';
      }
      case OrderEventType.AllocationComplete: {
        const payload = event.payload as IAllocationPayload;

        if (payload) {
          return `Allocation complete for invoice auditNo: ${payload!.invoiceAuditNo}
            and receipt auditNo: ${payload!.receiptAuditNo}`;
        }

        return 'Allocation Complete';
      }
      case OrderEventType.AllocationError: {
        const payload = event.payload as IAllocationPayload;

        if (payload) {
          return `Allocation failed for invoice auditNo: ${payload!.invoiceAuditNo}
            and receipt auditNo: ${payload!.receiptAuditNo}`;
        }

        return 'Allocation Failed';
      }

      // Radaro

      case OrderEventType.RadaroJobsAssigned: {
        const payload = event.payload as { jobIds: string[] }
        return `Radaro Jobs Assigned: ${(payload.jobIds ?? []).join(", ")}`;
      }
      case OrderEventType.RadaroJobsCleared: {
        const payload = event.payload as { jobIds: string[] }
        return `Radaro Jobs Cleared: ${(payload.jobIds ?? []).join(", ")}`;
      }

      case OrderEventType.PurchaseOrderUpdateRaised: {
        const updatePoRaisedPayload = event.payload as IUpdatePurchaseOrderPayload;
        if (updatePoRaisedPayload) {
          return `Update Purchase Order request raised for ${updatePoRaisedPayload.purchaseOrderNumber} for supplier ${updatePoRaisedPayload.supplierName}.`;
        }
        return 'Update Purchase Order Request Raised';
      }
      case OrderEventType.PurchaseOrderUpdateFailure: {
        const updatePoFailedPayload = event.payload as IUpdatePurchaseOrderPayload;
        if (updatePoFailedPayload) {
          return `Update Purchase Order request failed for ${updatePoFailedPayload.purchaseOrderNumber} for supplier ${updatePoFailedPayload.supplierName}.`;
        }
        return 'Update Purchase Order Failed';
      }
      case OrderEventType.PurchaseOrderUpdateSuccess: {
        const updatePoSuccessPayload = event.payload as IUpdatePurchaseOrderPayload;
        if (updatePoSuccessPayload) {
          return `Purchase Order ${updatePoSuccessPayload.purchaseOrderNumber} for supplier ${updatePoSuccessPayload.supplierName} updated.`;
        }
        return 'Updated Purchase Order';
      }
      case OrderEventType.PurchaseOrderUpdateEmailed: {
        const updatePoEmailedPayload = event.payload as IUpdatePurchaseOrderPayload;
        if (updatePoEmailedPayload) {
          return `Updated Purchase Order emailed to supplier ${updatePoEmailedPayload.supplierName} for purchase order ${updatePoEmailedPayload.purchaseOrderNumber}.`;
        }
        return 'Updated Purchase Order emailed.';
      }
      case OrderEventType.QuoteCancelled:
        return `Quote cancelled by ${event.repCode} ${event.payload ? (event.payload as IWorkflowPayload).reason : ''}`;
      case OrderEventType.ManualCompletion: {
        const manualCompletionPayload = event.payload as IManualCompletionPayload;
        const reason = manualCompletionPayload?.reason?.length
          ? ` (${manualCompletionPayload.reason})`
          : ''
        return `Order manually completed by ${event.repCode}.${reason}`;
      }
      case OrderEventType.Complete:
        return 'Order complete';
      case OrderEventType.GrnRaised: {
        const pl = event.payload as IGrnRaisedEventPayload;
        return `GRN Raised for PO '${pl.purchaseOrderNumber}', ASAP Job ID: '${pl.asapJobGuid || '---'}', Supplier '${
          pl.supplierName
        }'`;
      }
      case OrderEventType.GrnError: {
        const pl2 = event.payload as IUpdatePurchaseOrderPayloadPayload;
        return `GRN Failed for PO '${pl2.purchaseOrderNumber}', Supplier '${pl2.supplierName}'`;
      }
      case OrderEventType.ProductChecked:
        return `Product checked by ${event.repCode}.`;
      case OrderEventType.UnleashedOrderFailed:
        return `Order failed to be sent to Unleashed.`;
  }
  return '';
};

const describeInvoiceProcessingEvent = (payload: IInvoiceProcessingAuditPayload): string => {
  switch (payload.phase) {
    case 'RetryQueued':
      return `Invoice retry queued by ${payload.initiatingRepCode}.`;
    case 'RetryQueueResult':
      return sentenceWithDetail('Invoice retry queue failed', payload.errorSummary);
    case 'ProcessingStarted':
      return 'Invoice processing started.';
    case 'OperationResult':
      return describeInvoiceOperation(payload);
    case 'Deferred':
      return sentenceWithDetail('Invoice deferred', payload.errorSummary);
    case 'Failed':
      return sentenceWithDetail('Invoice failed', payload.errorSummary);
    case 'Blocked':
      return sentenceWithDetail('Invoice blocked', payload.errorSummary);
    case 'Completed':
      return 'Invoice completed.';
    default:
      return `Invoice processing ${payload.outcome.toLowerCase()}.`;
  }
};

const describeInvoiceOperation = (payload: IInvoiceProcessingAuditPayload): string => {
  const order = payload.externalOrderNumber
    ? `Unleashed sales order ${payload.externalOrderNumber}`
    : 'Unleashed sales order';
  const operation = payload.operation === 'Complete' ? 'completion' : 'creation';

  if (payload.outcome === 'Succeeded') {
    return `${order} ${payload.operation === 'Complete' ? 'completed' : 'created'}.`;
  }

  return sentenceWithDetail(`${order} ${operation} failed`, payload.errorSummary);
};

const sentenceWithDetail = (message: string, detail?: string): string => {
  const sentence = detail ? `${message}: ${detail.trim()}` : message;
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
};

const styles = (theme: Theme) =>
  createStyles({
    ...globalStyles,
  });

export default withStyles(styles)(OrderAuditInfo);
